import React, { useCallback, useState } from 'react';
import moment from 'moment';
import { Box, Button } from '@mui/material';
import { useQuery } from '@apollo/client';
import { QueryTableResponse } from 'global';
import { Refresh } from '@mui/icons-material';
import { DateFormats } from 'constants/common';
import { Listing } from 'components/shared/TableListing';

import { BPFAPINameSpase } from '../../types';
import { ChangelogFilters } from './ChangelogFilters';
import { changelogListingColumns } from './listingConfig';
import { bpfAPI, BpfChangelogViewModel } from '../../api';
import { BpfChangelogFiltersInput, BpfChangelogTableRow } from './types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';

interface Props {
  parentId?: string;
}

export const ChangelogListing = ({ parentId }: Props) => {
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 });
  const [filters, setFilters] = useState<BpfChangelogFiltersInput | undefined>(undefined);

  const { data, previousData, loading, refetch } = useQuery<BPFAPINameSpase<QueryTableResponse<BpfChangelogViewModel>>>(
    bpfAPI.changelogGetList,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        input: {
          pagination,
          ...(filters ? { filters } : {}),
          ...(parentId ? { id: parentId } : {}),
        },
      },
    }
  );

  const handleFiltersChanged = useCallback((nextFilters: BpfChangelogFiltersInput | undefined) => {
    setFilters(nextFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const items = data?.bpf?.result?.items ?? previousData?.bpf?.result?.items ?? [];

  const tableData: BpfChangelogTableRow[] = items.map(log => ({
    id: log.id,
    changes: log.changes,
    changeType: log.changeType,
    modifiedBy: log.modifiedBy?.fullName ?? '—',
    createdDateTime: moment(log.createdDateTime).format(DateFormats.DateTimeMedium24Hour),
  }));

  return (
    <CardContainer
      title="List"
      style={{ overflow: 'hidden' }}
      actionBtn={
        <Button startIcon={<Refresh />} onClick={() => refetch()} disabled={loading}>
          Update
        </Button>
      }
    >
      <Box sx={{ gap: '1rem', minHeight: 0, display: 'flex', height: '100%', flexDirection: 'column' }}>
        <ChangelogFilters loading={loading} onFiltersChanged={handleFiltersChanged} />

        <Box sx={{ flex: 1, minHeight: 0, height: 0 }}>
          <Listing<BpfChangelogTableRow>
            loading={loading}
            tableData={tableData}
            columns={changelogListingColumns}
            maxHeight="100%"
            pagination={{
              ...pagination,
              total: data?.bpf?.result?.pagination?.total ?? 0,
              onChange: nextValue => setPagination(prev => ({ ...prev, page: nextValue })),
            }}
          />
        </Box>
      </Box>
    </CardContainer>
  );
};
